import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { API_LIST_TICKETS } from "../../api/ticket.service";
import {
  normalizeAssignedTo,
  TICKET_AGENTS,
  TICKET_AGENT_OPTIONS,
} from "../../constants/ticketAgents";
import "./ticketsDashboard.css";

const toDateString = (value) => {
  if (!value) return null;
  const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return date.toISOString().substring(0, 10);
};

const parseDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const normalizeTicketsResponse = (response) => {
  const payload = response?.data || response || {};
  const rowsCandidates = [payload?.rows, payload?.data?.rows, payload?.tickets, payload?.data];
  const rows = rowsCandidates.find((item) => Array.isArray(item)) || [];
  const countCandidates = [payload?.count, payload?.data?.count, payload?.total];
  const countFound = countCandidates.find((item) => Number.isFinite(Number(item)));
  const count = countFound !== undefined ? Number(countFound) : rows.length;
  return { rows, count };
};

const isSlaBreached = (ticket) => {
  if (typeof ticket?.slaBreached === "boolean") return ticket.slaBreached;
  const due = parseDate(ticket?.slaDueAt);
  if (!due) return false;
  return due.getTime() < Date.now();
};

const avgMinutes = (rows, startField, endField) => {
  const diffs = rows
    .map((row) => {
      const start = parseDate(row?.[startField]);
      const end = parseDate(row?.[endField]);
      if (!start || !end || end < start) return null;
      return (end.getTime() - start.getTime()) / (1000 * 60);
    })
    .filter((value) => Number.isFinite(value));

  if (!diffs.length) return 0;
  return Math.round(diffs.reduce((sum, value) => sum + value, 0) / diffs.length);
};

const toPercent = (value, total) => (total ? Math.round((value / total) * 100) : 0);

const normalizeStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase().replace(/[\s_]+/g, "");
  if (normalized === "open") return "open";
  if (normalized === "inprogress") return "in_progress";
  if (normalized === "resolved") return "resolved";
  if (normalized === "closed") return "closed";
  return "";
};

const getStatusCounts = (rows) => {
  const counts = {
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  };

  rows.forEach((row) => {
    const status = normalizeStatus(row?.status);
    if (status === "open") counts.open += 1;
    if (status === "in_progress") counts.inProgress += 1;
    if (status === "resolved") counts.resolved += 1;
    if (status === "closed") counts.closed += 1;
  });

  return counts;
};

const getPriorityCounts = (rows) => {
  const counts = {
    urgent: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  rows.forEach((row) => {
    const priority = String(row?.priority || "").trim().toLowerCase();
    if (priority === "urgent") counts.urgent += 1;
    if (priority === "high") counts.high += 1;
    if (priority === "medium") counts.medium += 1;
    if (priority === "low") counts.low += 1;
  });

  return counts;
};

const buildTrend = (rows, trendRange, from, to) => {
  const end = startOfDay(new Date());
  let start;

  if (trendRange === "last7") {
    start = new Date(end);
    start.setDate(end.getDate() - 6);
  } else if (from && to) {
    start = startOfDay(from);
    const customEnd = startOfDay(to);
    if (customEnd < start) {
      start = startOfDay(customEnd);
    }
    end.setTime(startOfDay(customEnd).getTime());
  } else {
    start = new Date(end);
    start.setDate(end.getDate() - 11);
  }

  const labels = [];
  const data = [];
  const maxDays = 31;
  const cursor = new Date(start);
  const ticketsByDay = new Map();

  rows.forEach((row) => {
    const created = parseDate(row?.createdAt);
    if (!created) return;
    const key = created.toISOString().substring(0, 10);
    ticketsByDay.set(key, (ticketsByDay.get(key) || 0) + 1);
  });

  let guard = 0;
  while (cursor <= end && guard < maxDays) {
    const key = cursor.toISOString().substring(0, 10);
    labels.push(
      cursor.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    );
    data.push(ticketsByDay.get(key) || 0);
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }

  if (!data.length) {
    return { labels: ["No Data"], data: [0] };
  }

  return { labels, data };
};

const buildTrendPath = (values) => {
  const height = 180;
  const width = 520;
  const maxVal = Math.max(...values, 1);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  return values
    .map((val, idx) => {
      const x = idx * stepX;
      const y = height - (val / maxVal) * height;
      return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");
};

export const TicketsDashboard = () => {
  const navigate = useNavigate();
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [trendRange, setTrendRange] = useState("last7");
  const [agentFilter, setAgentFilter] = useState("");

  const [allRows, setAllRows] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    let alive = true;

    const fetchAllTickets = async () => {
      setLoadingData(true);
      setFetchError("");

      try {
        let page = 0;
        const limit = 100;
        let fetchedCount = 0;
        let total = Infinity;
        const rowsById = new Map();
        const rowsWithoutId = [];

        while (fetchedCount < total && page < 100) {
          const payload = {
            from: toDateString(from) || undefined,
            to: toDateString(to) || undefined,
            page,
            limit,
          };

          const response = await API_LIST_TICKETS(payload);
          const normalized = normalizeTicketsResponse(response);
          normalized.rows.forEach((row) => {
            if (row?.id === undefined || row?.id === null) {
              rowsWithoutId.push(row);
              return;
            }
            rowsById.set(String(row.id), row);
          });
          fetchedCount += normalized.rows.length;
          total = normalized.count;

          if (!normalized.rows.length) break;
          page += 1;
        }

        if (!alive) return;
        setAllRows([...rowsById.values(), ...rowsWithoutId]);
      } catch (error) {
        if (!alive) return;
        setFetchError(error?.response?.data?.message || error?.message || "Unable to load dashboard data.");
        setAllRows([]);
      } finally {
        if (alive) setLoadingData(false);
      }
    };

    fetchAllTickets();

    return () => {
      alive = false;
    };
  }, [from, to]);

  const agentScopedRows = useMemo(() => {
    const selected = normalizeAssignedTo(agentFilter);
    if (!selected) return allRows;
    return allRows.filter(
      (row) => normalizeAssignedTo(row?.assignedTo) === selected
    );
  }, [agentFilter, allRows]);

  const metrics = useMemo(() => {
    const statusCounts = getStatusCounts(agentScopedRows);
    const priorityCounts = getPriorityCounts(agentScopedRows);

    return {
      totalTickets: agentScopedRows.length,
      openTickets: statusCounts.open,
      inProgressTickets: statusCounts.inProgress,
      resolvedTickets: statusCounts.resolved,
      closedTickets: statusCounts.closed,
      slaBreachedTickets: agentScopedRows.filter((row) => isSlaBreached(row)).length,
      avgFirstResponseTimeMinutes: avgMinutes(
        agentScopedRows,
        "createdAt",
        "firstResponseAt"
      ),
      avgResolutionTimeMinutes: avgMinutes(
        agentScopedRows,
        "createdAt",
        "resolvedAt"
      ),
      priorityCounts,
    };
  }, [agentScopedRows]);

  const trend = useMemo(
    () => buildTrend(agentScopedRows, trendRange, from, to),
    [agentScopedRows, from, to, trendRange]
  );

  const trendPath = useMemo(() => buildTrendPath(trend.data), [trend.data]);

  const statusDonut = useMemo(() => {
    const total = metrics.totalTickets;
    const openPct = toPercent(metrics.openTickets, total);
    const progPct = toPercent(metrics.inProgressTickets, total);
    const resolvedPct = toPercent(metrics.resolvedTickets, total);
    const closedPct = toPercent(metrics.closedTickets, total);

    return {
      openPct,
      progPct,
      resolvedPct,
      closedPct,
      gradient: `conic-gradient(
        #e84c3d 0% ${openPct}%,
        #f39c12 ${openPct}% ${openPct + progPct}%,
        #2ecc71 ${openPct + progPct}% ${openPct + progPct + resolvedPct}%,
        #3b5bdb ${openPct + progPct + resolvedPct}% 100%
      )`,
    };
  }, [metrics]);

  const priorityDonut = useMemo(() => {
    const total = metrics.totalTickets;
    const urgentPct = toPercent(metrics.priorityCounts.urgent, total);
    const highPct = toPercent(metrics.priorityCounts.high, total);
    const mediumPct = toPercent(metrics.priorityCounts.medium, total);
    const lowPct = toPercent(metrics.priorityCounts.low, total);

    return {
      urgentPct,
      highPct,
      mediumPct,
      lowPct,
      gradient: `conic-gradient(
        #e84c3d 0% ${urgentPct}%,
        #f39c12 ${urgentPct}% ${urgentPct + highPct}%,
        #3b5bdb ${urgentPct + highPct}% ${urgentPct + highPct + mediumPct}%,
        #2ecc71 ${urgentPct + highPct + mediumPct}% 100%
      )`,
    };
  }, [metrics]);

  const agentPerformanceRows = useMemo(
    () =>
      TICKET_AGENTS.map((agent) => {
        const agentRows = allRows.filter(
          (row) => normalizeAssignedTo(row?.assignedTo) === agent.value
        );
        const resolved = agentRows.filter(
          (row) => normalizeStatus(row?.status) === "resolved"
        ).length;
        const slaBreaches = agentRows.filter((row) => isSlaBreached(row)).length;

        return {
          key: agent.key,
          label: agent.label,
          ticketsAssigned: agentRows.length,
          ticketsResolved: resolved,
          slaBreaches,
        };
      }),
    [allRows]
  );

  return (
    <div className="tickets-dashboard">
      {loadingData ? (
        <div className="loading-container">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </div>
      ) : null}

      <div className="tickets-dashboard-header">
        <div className="ticket-breadcrumb">
          <strong>Tickets Dashboard</strong>
        </div>
        <div className="tickets-dashboard-filters">
          <Dropdown
            value={agentFilter}
            options={TICKET_AGENT_OPTIONS}
            onChange={(event) => setAgentFilter(event.value || "")}
            placeholder="Select Agent"
            showClear
          />
          <Calendar
            value={from}
            onChange={(event) => setFrom(event.value)}
            placeholder="From"
            dateFormat="yy-mm-dd"
            showIcon
          />
          <Calendar
            value={to}
            onChange={(event) => setTo(event.value)}
            placeholder="To"
            dateFormat="yy-mm-dd"
            showIcon
          />
          <button
            className="manage-tickets-btn"
            onClick={() => navigate("/tickets")}
          >
            Manage Tickets
          </button>
        </div>
      </div>

      {fetchError ? <div className="dashboard-inline-error">{fetchError}</div> : null}

      <div className="tickets-kpi-row">
        <div className="kpi-card total">
          <div className="kpi-icon">T</div>
          <div>
            <span>Total Tickets</span>
            <strong>{metrics.totalTickets}</strong>
          </div>
        </div>
        <div className="kpi-card open">
          <div className="kpi-icon">O</div>
          <div>
            <span>Open Tickets</span>
            <strong>{metrics.openTickets}</strong>
          </div>
        </div>
        <div className="kpi-card progress">
          <div className="kpi-icon">P</div>
          <div>
            <span>In Progress</span>
            <strong>{metrics.inProgressTickets}</strong>
          </div>
        </div>
        <div className="kpi-card resolved">
          <div className="kpi-icon">R</div>
          <div>
            <span>Resolved</span>
            <strong>{metrics.resolvedTickets}</strong>
          </div>
        </div>
        <div className="kpi-card closed">
          <div className="kpi-icon">C</div>
          <div>
            <span>Closed</span>
            <strong>{metrics.closedTickets}</strong>
          </div>
        </div>
        {/* <div className="kpi-card response">
          <div className="kpi-icon">FR</div>
          <div>
            <span>Avg First Response</span>
            <strong>{(metrics.avgFirstResponseTimeMinutes / 60 || 0).toFixed(1)} hrs</strong>
          </div>
        </div> */}
      </div>

      <div className="tickets-grid">
        <div className="tickets-panel">
          <div className="panel-header">
            <h3>Tickets Trend</h3>
            <div className="trend-tabs">
              <button
                className={trendRange === "last7" ? "active" : ""}
                onClick={() => setTrendRange("last7")}
              >
                Last 7 Days
              </button>
              <button
                className={trendRange === "custom" ? "active" : ""}
                onClick={() => setTrendRange("custom")}
              >
                Custom
              </button>
            </div>
          </div>
          <div className="trend-chart">
            <svg viewBox="0 0 520 180" preserveAspectRatio="none">
              <path d={trendPath} className="trend-line" />
            </svg>
            <div className="trend-labels">
              {trend.labels.map((label, idx) => (
                <span key={`${label}-${idx}`}>{label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="tickets-panel">
          <h3>Tickets by Status</h3>
          <div className="donut-wrap">
            <div className="donut" style={{ background: statusDonut.gradient }}>
              <div className="donut-hole">
                <span>{metrics.totalTickets}</span>
              </div>
            </div>
            <div className="donut-legend">
              <div>
                <span className="dot open" /> Open {statusDonut.openPct}%
              </div>
              <div>
                <span className="dot progress" /> In Progress {statusDonut.progPct}%
              </div>
              <div>
                <span className="dot resolved" /> Resolved {statusDonut.resolvedPct}%
              </div>
              <div>
                <span className="dot closed" /> Closed {statusDonut.closedPct}%
              </div>
            </div>
          </div>
        </div>

        <div className="tickets-panel">
          <h3>Tickets by Priority</h3>
          <div className="donut-wrap">
            <div className="donut" style={{ background: priorityDonut.gradient }}>
              <div className="donut-hole">
                <span>{metrics.totalTickets}</span>
              </div>
            </div>
            <div className="donut-legend">
              <div>
                <span className="dot urgent" /> Urgent {priorityDonut.urgentPct}%
              </div>
              <div>
                <span className="dot high" /> High {priorityDonut.highPct}%
              </div>
              <div>
                <span className="dot medium" /> Medium {priorityDonut.mediumPct}%
              </div>
              <div>
                <span className="dot low" /> Low {priorityDonut.lowPct}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tickets-grid two-col">
        <div className="tickets-panel">
          <h3>SLA Overview</h3>
          <div className="sla-row">
            <div>
              <span>Within SLA</span>
              <strong>
                {metrics.totalTickets
                  ? Math.round(
                      ((metrics.totalTickets - metrics.slaBreachedTickets) /
                        metrics.totalTickets) *
                        100
                    )
                  : 0}
                %
              </strong>
            </div>
            <div>
              <span>Avg Resolution Time</span>
              <strong>{(metrics.avgResolutionTimeMinutes / 60 || 0).toFixed(1)} hrs</strong>
            </div>
            <div>
              <span>SLA Breaches</span>
              <strong>{metrics.slaBreachedTickets}</strong>
            </div>
          </div>
        </div>

        <div className="tickets-panel">
          <h3>Agent Performance</h3>
          <p style={{ marginTop: "-4px", marginBottom: "10px", fontSize: "12px", opacity: 0.75 }}>
            All agents (not affected by Select Agent)
          </p>
          <table className="agent-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Tickets Assigned</th>
                <th>Tickets Resolved</th>
                <th>SLA Breaches</th>
              </tr>
            </thead>
            <tbody>
              {agentPerformanceRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  <td>{row.ticketsAssigned}</td>
                  <td>{row.ticketsResolved}</td>
                  <td>{row.slaBreaches}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
